import { t } from 'i18next';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../../hooks';
import { ContractStatus, ContractType, FileInfo, FSObjectCategory, useCreateContractMutation, useCreateUploadV1Mutation, useDeleteContractMutation, useDeleteFileMutation, useGetContractAvailUnitsQuery, useLazyGetContractQuery, useLazyGetFilesQuery, useUpdateContractMutation } from '../../../services/content_v1';
import { AccountResType, useGetAccountListQuery, useGetUserAccountsManageQuery } from '../../../services/users_v1';
import { useAlert } from '../../alert';
import { extractErrorDetail, formatBytes, parseDate, toMplatBackendDate, useIsAdmin } from '../../common';
import Button from '../Button/Button';
import { DatePicker } from '../DatePicker';
import Form from '../Form';
import FormUploaderZone from '../FormUploaderZone';
import Label from '../Label';
import { Modal } from '../Modal';
import MultiSelect from '../MultiSelect';
import { RadioInput } from '../RadioInput';
import Select from '../Select';
import TextInput from '../TextInput';
import { CreateContractModalProps } from './types';


const AVAIL_DOC_FORMATS = ['.doc', '.docx', '.pdf', '.xls', '.odt', '.rtf', '.tiff', '.png', '.bmp', '.jpg'];

const FormUploaderZoneHint = () => <>
    <Trans>
        Drag files here
    </Trans>
    <br />
    <span>
        <Trans>
            or select on computer
        </Trans>
    </span>
</>;

export function CreateContractModal({ isOpen, onClose, onSuccess, contractId, defaultConsumer, defaultProvider }: CreateContractModalProps) {
    const alert = useAlert();
    const auth = useAuth();
    const isAdmin = useIsAdmin();
    const { data: providers } = useGetUserAccountsManageQuery();
    const { data: consumers } = useGetAccountListQuery({ offset: 0, limit: -1, rtype: AccountResType.CONSUMER });
    const { data: listAvailTUnits } = useGetContractAvailUnitsQuery();
    const [fetchFiles, { data: listUnsavedFiles }] = useLazyGetFilesQuery();
    const [fetchContract, { data: contract }] = useLazyGetContractQuery();
    const [createContract] = useCreateContractMutation();
    const [updateContract] = useUpdateContractMutation();
    const [createUpload] = useCreateUploadV1Mutation();
    const [deleteFile] = useDeleteFileMutation();
    const [deleteContract] = useDeleteContractMutation();

    const now = new Date();

    const [provider, setProvider] = useState(defaultProvider || auth.account);
    const [consumer, setConsumer] = useState(defaultConsumer || -1);
    const [nDoc, setNDoc] = useState('');
    const [spec, setSpec] = useState('');
    const [status, setStatus] = useState(ContractStatus.AGREEMENT);
    const [permPeriod, setPermPeriod] = useState(1);
    const [startDate, setStartDate] = useState<Date | undefined>(now);
    const [endDate, setEndDate] = useState<Date | undefined>(new Date(now.getFullYear() + 1, now.getMonth(), now.getDate()));
    const [units, setUnits] = useState<number[]>([]);
    const [files, setFiles] = useState<number[]>([]);
    const [filesInfo, setFilesInfo] = useState<FileInfo[]>([]);
    const [unsavedFilesUUID, setUnsavedFilesUUID] = useState<string[]>([]);
    const [error, setError] = useState('');

    const availProviders = useMemo(() => {
        return isAdmin ? [auth.account] : (providers?.results.data.map((p) => ({ value: p.id, label: p.name })) || []);
    }, [isAdmin, auth.account, providers]);

    const availConsumers = useMemo(() => {
        return consumers?.results.data.map((p) => ({ value: p.id, label: p.name })) || [];
    }, [consumers]);

    const availUnits = useMemo(() => {
        return listAvailTUnits?.results.data.filter((tv) => provider === Number(auth.account) || tv.rowners?.includes(provider)).map((tv) => ({ value: tv.id, label: tv.title })) || [];
    }, [listAvailTUnits, provider]);

    const onCreateFile = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        if (e.target.files) {
            const uuid = uuidv4();
            const file = e.target.files[0];

            const correct = AVAIL_DOC_FORMATS.find((ext) => file.name.endsWith(ext));

            if (correct) {
                createUpload({
                    uuid: uuid,
                    name: file.name,
                    cat: FSObjectCategory.DOCUMENT,
                    file: file
                }).unwrap().then((d) => setUnsavedFilesUUID((prev) => [...prev, d.results.uuid])).catch((err) => alert.error(extractErrorDetail(err)));
            } else {
                alert.error('Ай ай ай, ну что вы за человек, просили же вас грузить только документы');
            }
        }
    }, []);

    const onCreate = useCallback(() => {
        setError('');
        const action = contractId ? updateContract : createContract;
        action({
            id: contractId,
            consumer: consumer,
            provider: provider,
            cotype: ContractType.LICENSE,
            number: nDoc,
            additional: spec,
            comment: '',
            content: units,
            validity: {
                lower: toMplatBackendDate(startDate, true),
                upper: permPeriod === 1 ? toMplatBackendDate(endDate, true) : undefined,
            },
            products: [],
            status: status,
            files: [...files, ...(listUnsavedFiles?.results.data.filter((f) => unsavedFilesUUID.includes(f.upload_uuid)).map((f) => f.id) || [])]
        }).unwrap().then((d) => { onClose(); onSuccess && onSuccess(d.results.id) }).catch((e) => setError(extractErrorDetail(e)));
    }, [provider, consumer, nDoc, spec, status, units, files, unsavedFilesUUID, listUnsavedFiles, permPeriod, startDate, endDate]);

    const onDelete = useCallback(() => {
        setError('');
        if (contractId) {
            const ret = confirm(t('order:ORDER_DELETE_CONFIRM_MESSAGE'));
            if (ret) {
                deleteContract(contractId).unwrap()
                    .then(onClose).catch((e) => setError(extractErrorDetail(e)));
            }
        }
    }, [contractId]);

    useEffect(() => {
        if (contract?.results) {
            const c = contract.results;
            setConsumer(c.consumer.id);
            setProvider(c.provider.id);
            setSpec(c.additional);
            setNDoc(c.number);
            setStartDate(parseDate(c.validity?.lower) || new Date());
            if (c.validity?.upper) {
                setEndDate(parseDate(c.validity?.upper));
            } else {
                setPermPeriod(2);
            }
            setUnits(c.content?.map((u) => u.id) || []);
            setFiles(c.files.map((f) => f.id));
            setFilesInfo(c.files);
            setStatus(c.status);
        }
    }, [contract]);

    useEffect(() => {
        if (contractId) {
            fetchContract(contractId);
        }
    }, [contractId]);

    useEffect(() => {
        if (unsavedFilesUUID.length > 0) {
            fetchFiles({ offset: 0, limit: -1, upload_uuid: unsavedFilesUUID.join(',') });
        }
    }, [unsavedFilesUUID]);

    return (
        <Modal className={'popup_message'} isOpen={isOpen} onClose={() => onClose()}>
            <Form action="#" className="order__permission _new">
                <h2 className="popup__heading">
                    {contractId ?
                        <Trans>Edit document</Trans>
                        :
                        <Trans>New document</Trans>
                    }
                </h2>
                <h3><Trans>Provider</Trans></h3>
                <Select value={provider} onChange={(v) => setProvider(v)} options={availProviders} />

                <h3><Trans>Consumer</Trans></h3>
                <Select value={consumer} onChange={(v) => setConsumer(v)} options={availConsumers} />

                <h3><Trans>Contract number</Trans></h3>
                <TextInput value={nDoc} onChange={(e) => setNDoc(e.target.value)} placeholder="Укажите номер" />

                <div className="permission__dates">
                    <h3><Trans>Term</Trans></h3>
                    <div className="form__item">
                        <Label><Trans>order:CONTRACT_START_DATE</Trans></Label>
                        <DatePicker handleEdit simple fullSize value={startDate} placeholder="" setValue={(v) => setStartDate(v)} />
                    </div>
                    <div className="form__item">
                        <Label><Trans>order:CONTRACT_END_DATE</Trans></Label>
                        <div className="permission__dates-end">
                            <Select value={permPeriod} options={[{ value: 1, label: t('order:UNTIL_DATE') }, { value: 2, label: t('order:UNTIL_REVOKE') }]} onChange={(v) => setPermPeriod(v)} />
                            <DatePicker handleEdit className={permPeriod === 1 ? '_show' : ''} simple fullSize minDate={startDate} value={endDate} placeholder="" setValue={(v) => setEndDate(v)} />
                        </div>
                    </div>
                </div>

                <h3><Trans>Content</Trans></h3>
                <MultiSelect value={units} setValue={(v) => setUnits(v)} options={availUnits} />

                <h3><Trans>order:SPEC_CONDITIONS</Trans></h3>
                <TextInput value={spec} onChange={(e) => setSpec(e.target.value)} placeholder={t('order:SPECIFY_CONDITIONS')} />

                <h3><Trans>Status</Trans></h3>
                <RadioInput
                    className="form__checkbox-list"
                    value={status}
                    items={[
                        { value: ContractStatus.CONCLUDED, label: t('order:CONTRACT_CONCLUDED') },
                        { value: ContractStatus.AGREEMENT, label: t('order:CONTRACT_AGREEMENT') },
                        { value: ContractStatus.TERMINATED, label: t('order:CONTRACT_TERMINATED') },
                    ]}
                    onChange={(v) => setStatus(v)}
                />

                <div className="form__item">
                    <FormUploaderZone
                        hint={<FormUploaderZoneHint />}
                    >
                        <input
                            type="file"
                            className="form__uploader-input"
                            accept={AVAIL_DOC_FORMATS.join(',')}
                            onChange={onCreateFile}
                        />
                        <Trans>+ Upload files</Trans>
                    </FormUploaderZone>
                </div>
                <div className="order__files">
                    {unsavedFilesUUID.length > 0 && listUnsavedFiles?.results.data.map((f) => <div key={f.upload_uuid} className="order__files-item">
                        <div className="order__files-item-name">{f.name}</div>
                        <div className="order__files-item-size">{formatBytes(f.size)}</div>
                        <div className="order__files-item-remove" onClick={() => deleteFile(f.id).unwrap().then(() => setUnsavedFilesUUID(unsavedFilesUUID.filter((uuid) => uuid !== f.upload_uuid))).catch((err) => alert.error(extractErrorDetail(err)))}>
                            <Trans>Delete</Trans>
                        </div>
                    </div>)}
                    {filesInfo.filter((f) => files.includes(f.id)).map((f) => <div key={f.id} className="order__files-item">
                        <div className="order__files-item-name">{f.name}</div>
                        <div className="order__files-item-size">{formatBytes(f.size)}</div>
                        <div className="order__files-item-remove" onClick={() => setFiles(files.filter((fid) => fid !== f.id))}>
                            <Trans>Delete</Trans>
                        </div>
                    </div>)}
                </div>
                {error &&
                    <div className="order__permission-error">{error}</div>
                }
                <div className="order__permission-cta">
                    <div className="order__permission-cta-item">
                        <Button type="button" onClick={onCreate}>
                            {contractId ?
                                <Trans>Redact</Trans>
                                :
                                <Trans>Create</Trans>
                            }
                        </Button>
                    </div>
                    {contractId && contract?.results.permits.length === 0 &&
                        <div className="order__permission-cta-item">
                            <Button className="_bd-red" type="button" onClick={onDelete}><Trans>Delete</Trans></Button>
                        </div>}
                    <div className="order__permission-cta-item">
                        <Button className="_bd" type="button" onClick={onClose}><Trans>Cancel</Trans></Button>
                    </div>
                </div>
            </Form>
        </Modal>
    );
}
